import type { KustomerOnSatisfactionReceived } from 'embed/kustomerCore';
import type { ZendeskDepartments } from 'embed/zendesk';
import type { ConfigState } from 'reducers/config';

export type ComponentTypes =
  | HandoffComponentTypes
  | 'article'
  | 'article_suggestion'
  | 'buttons'
  | 'csat_survey'
  | 'DATE'
  | 'DROPDOWN'
  | 'EMAIL'
  | 'form'
  | 'hyperlink_redirect'
  | 'LONG_TEXT'
  | 'open_url'
  | 'PHONE_NUMBER'
  | 'PHONE'
  | 'NUMBER'
  | 'restart_conversation'
  | 'SHORT_TEXT'
  | 'CHECKBOX'
  | 'text_message'
  | 'ujet_chat_handoff'
  | 'zendesk_attachment_upload'
  | 'attachment_upload'
  | 'LIST'
  | 'trigger_event'
  | 'MULTI_SELECT_LIST'
  | 'visible_only_to_admins'
  | 'image'
  | 'embed'
  | 'forethought_live_chat_handoff'
  | 'sunco_live_chat'
  | 'user_message'
  | 'dynamic_card'
  | 'knowledge_retrieval_stream'
  | 'csat_trigger_point'
  | 'event_message'
  | 'interactive_email_inquiry'
  | 'end_interactive_email_chat'
  | 'attachment_analysis_upload'
  | 'kustomer_satisfaction_survey'
  | 'analyzing_your_attachments'
  | 'conversation_oauth_sign_in'
  | 'autoflow_stream'
  | 'zendesk_talk_handoff'
  | 'zendesk_sip_call'
  | 'zendesk_sip_call_trigger'
  | 'hubspot_chat_handoff'
  | 'product_recommendation'
  | 'browser_agent';

export type HandoffComponentTypes =
  | 'brand_embassy_chat_handoff'
  | 'drift_chat_handoff'
  | 'intercom_chat_handoff'
  | 'kustomer_chat_handoff'
  | 'salesforce_chat_handoff'
  | 'snapengage_chat_handoff'
  | 'zendesk_chat_handoff'
  | 'zendesk_messaging_handoff'
  | 'zendesk_talk_handoff'
  | 'generic_handoff'
  | 'gorgias_chat_handoff'
  | 'talkdesk_chat_handoff'
  | 'freshchat_chat_handoff'
  | 'salesforce_messaging_handoff'
  | 'live_chat_chat_handoff'
  | 'gladly_chat_handoff'
  | 'hubspot_chat_handoff';

export type HandoffIntegrations =
  | 'brand_embassy'
  | 'drift'
  | 'generic'
  | 'intercom'
  | 'kustomer'
  | 'salesforce'
  | 'snap_engage'
  | 'zendesk'
  | 'zendesk_messaging'
  | 'zendesk_talk'
  | 'ujet'
  | 'gorgias'
  | 'talkdesk'
  | 'freshchat'
  | 'salesforce_messaging'
  | 'live_chat'
  | 'forethought_live_chat'
  | 'sunco-live-chat'
  | 'zendesk-zopim-live-chat'
  | 'intercom-live-chat'
  | 'freshchat-live-chat'
  | 'kustomer-live-chat'
  | 'gladly'
  | 'hubspot';

export type GenericHandoffWidgetOption = 'hide' | 'minimize' | 'nothing';

/**
 * Tracking events that we currently only track the event type for.
 */
type PlainTrackingEventTypes =
  | 'widget-customer-ended-live-chat'
  | 'widget-customer-closed-proactive-bubbles'
  | 'widget-customer-ask-question'
  | 'widget-customer-proactive-bubbles-shown'
  | 'widget-banner-image-clicked'
  | 'widget-banner-image-closed';

/**
 * @internal Used in tests, mark as internal for knip.
 */
export type TrackingEventTypes =
  | PlainTrackingEventTypes
  | 'widget-opened'
  | 'widget-customer-agent-chat'
  | 'widget-customer-article-suggestion-helped'
  | 'widget-customer-article-suggestion-unhelpful'
  | 'widget-customer-entered-intent-by-button'
  | 'widget-customer-entered-intent-by-proactive-bubble'
  | 'widget-customer-handoff'
  | 'go-to-article'
  | 'widget-csat-submitted'
  | 'widget-zendesk-update-settings'
  | 'widget-zendesk-attachment-upload'
  | 'widget-disabled-by-local-storage'
  | 'widget-csat-opened'
  | 'widget-handoff-auth-failure'
  | 'widget-handoff-auth-success'
  | 'widget-error-banner-visible'
  | 'widget-live-chat-send-message'
  | 'widget-start-handoff'
  | 'widget-smooch-event'
  | 'widget-live-chat-event'
  | 'widget-handoff-event'
  | 'zopim-agent-session-mismatch'
  | 'widget-zopim-event'
  | 'widget-conversation-cleared'
  | 'proactive-trigger'
  | 'widget-salesforce-event'
  | 'widget-zendesk-event';

export type HandoffConfirmation = 'yes_resolve' | 'no_resolve';

export type LaunchStyle = 'icon' | 'floating_bar';

export type Size = 'standard' | 'large';

export type Placement =
  | 'bottom_right'
  | 'bottom_left'
  | 'top'
  | 'middle'
  | 'bottom';

export type Font = 'Plus Jakarta Sans' | string;

export type WidgetStyle = 'standard' | 'floating_bar' | 'mobile';

interface WorkflowConfigArgs {
  banner_image_config: {
    alt_text: string | null;
    image: string | null;
    is_enabled: boolean;
    link: string | null;
  };
  colors?: {
    background: string | null;
    danger: string | null;
    theme: string | null;
  };
  config_params: ContextVariables;
  conversation_id: string;
  csat_config: CsatConfigRevamped;
  csat_survey_config: {
    is_enabled: boolean;
    num_of_steps_required: number;
    should_only_show_on_close: boolean;
  };
  disable_forethought_branding: boolean;
  display_proactive_chat: boolean;
  display_proactive_chat_intents: boolean;
  font: Font;
  forethought_text?: {
    additional_feedback_dialog_title: string;
    back_to_chat: string;
    close_button_title: string;
    free_form_input_placeholder: string;
    free_form_input_placeholder_in_progress: string;
    free_form_input_placeholder_not_started: string;
    sources: string;
    submit_button_title: string;
    tag_selection_button_greeting_message: string;
    tag_selection_greeting_message: string;
  };
  free_form_intent_detection_enabled?: boolean;
  greeting_line?: string;
  images?: {
    agent_chat: string | null;
    header: string | null;
    help_button_hovered: string | null;
  };
  initial_widget_components?: WidgetComponent[];
  intentId: string | null | undefined;
  interactive_email_static_text?: {
    cancel: string;
    end_chat: string;
    end_chat_description: string;
    feedback_description: string;
    is_issue_resolved: string;
    no: string;
    yes: string;
  };
  intro_config: {
    image: {
      enabled: boolean;
      value: string;
    };
    subtitle: {
      enabled: boolean;
      value: string;
    };
    title: {
      enabled: boolean;
      value: string;
    };
  };
  launch_style: LaunchStyle;
  logrocket_enabled: boolean;
  on_load_config: OnLoadConfig;
  persistence_duration_minutes: number;
  persistence_enabled: boolean;
  placement: Placement;
  privacy_consent_config: PrivacyConsentConfig | null;
  proactive_chat_greeting: string;
  quick_feedback_config: {
    feedback_prompt: string;
    hover_label_bad: string;
    hover_label_good: string;
    is_enabled: boolean;
    negative_routing_intent_workflow_id: string | null;
    question_for_high_ratings: string;
    question_for_low_ratings: string;
    should_request_feedback_for_high_ratings: boolean;
    should_request_feedback_for_low_ratings: boolean;
    style: 'thumbs' | 'bw_smile' | 'hearts';
  };
  reset_widget_on_restart: boolean;
  size: Size;
  tag_selection_enabled: boolean;
  text?: {
    tab_name: string | null;
  };
  top_n_intents?: Array<{
    href?: string | null;
    intent_id: string;
    intent_name: string;
  }>;
  use_help_button_image_as_icon?: boolean;
  widget_article_search_config: {
    enabled: boolean;
  };
  widget_enabled: boolean;
  workflow_params?: ContextVariables;
  workflow_tags?: Array<string>;
}

interface WorkflowConfigResponse extends WorkflowConfigArgs {
  enabled_feature_flags: string[];
  external_event_automations: ExternalEventAutomation[];
  proactive_triggers: ProactiveTrigger[];
}

export type FilteredWorkflowConfig = Omit<
  WorkflowConfigArgs,
  'conversation_id'
>;

interface PrivacyConsentConfig {
  background_color: string;
  call_to_action_label: string;
  is_banner_dismissible: boolean;
  placement: 'top' | 'bottom';
  privacy_policy: string;
  prompt_header: string;
  show_when_conversation_starts: boolean;
  style: 'call_to_action' | 'sticky_banner';
}

interface CsatConfigRevamped {
  is_enabled: boolean;
  negative_rating_routing_intent?: string;
  no_resolve_option: string;
  one_to_five_labels: string[];
  options_for_resolution_confirmation: string[];
  question_for_additional_feedback: string;
  question_for_high_ratings: string;
  question_for_low_ratings: string;
  question_for_resolution_confirmation: string;
  rating_question: string;
  reasons_for_high_ratings: string[];
  reasons_for_low_ratings: string[];
  scale: 'one_to_two' | 'one_to_five';
  should_request_additional_feedback: boolean;
  should_request_feedback_for_high_ratings: boolean;
  should_request_feedback_for_low_ratings: boolean;
  should_request_resolution_confirmation: boolean;
  should_show_submit_confirmation_message: boolean;
  should_use_rating_based_submit_message: boolean;
  show_csat_after_n_interactions: number;
  show_on_header: boolean;
  show_when_agent_chat_ends: boolean;
  show_when_widget_minimizes: boolean;
  style: 'star' | 'smile' | 'bw_smile' | 'thumbs';
  submit_confirmation_message: string;
  submit_message_for_high_ratings: string;
  submit_message_for_low_ratings: string;
  yes_resolve_option: string;
}

export interface ContextVariableConfigurationFields {
  is_alphanumeric?: boolean;
  max_length?: number;
  min_length?: number;
}

interface FormFields {
  configuration_fields?: ContextVariableConfigurationFields;
  dropdown_options?: Array<DropdownItem>;
  input_type: ComponentTypes;
  list_type_options?: ListOption[];
  output_variable: string;
  placeholder: string;
  pre_populated_value?: string;
}

interface ContextVariables {
  [key: string]: string | boolean | number | string[];
}

export interface ArticleStepData {
  current_step_id: string;
  intent_workflow_id: string;
  intent_workflow_version: number;
  org_id: number;
  solve_widget_product: string;
}

interface ButtonOptions {
  display_name: string;
  url?: string;
  url_config?: {
    target_attribute: '_blank' | '_self' | '_parent' | '_top';
  };
  value: string;
}

interface FormStepComponent {
  component_fields: {
    forethought_text: FormStepComponentText;
    form_fields: Array<FormFields>;
    prompt: string;
    quotedMessageId?: string; // Used by Sunco One Chat and Salesforce One Chat
  } & Pick<
    UserMessageComponent['component_fields'],
    | 'agentIcon'
    | 'agentName'
    | 'should_render_as_markdown'
    | 'should_render_single_new_line_characters'
  >;
  component_type: ComponentTypes;
  contextVariables?: ContextVariables;
  delay?: number;
  hideTimestamp?: boolean;
  isInAgentConvo?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  neverMindSelection?: string;
  query?: string;
  should_render_as_markdown?: boolean;
  timestamp?: number;
}

interface FormStepComponentText {
  checkbox_no: string;
  checkbox_placeholder: string;
  checkbox_yes: string;
  dropdown_back: string;
  email_error: string;
  never_mind: string;
  number_error: string;
  phone_error: string;
  required_error: string;
  required_label: string;
  short_text_alphanumeric_error: string;
  short_text_equal_error: string;
  short_text_max_error: string;
  short_text_min_error: string;
  submit_button: string;
}

export type ReadArticleActions = 'new_tab' | 'expand_in_widget' | 'none';

interface ArticleCarouselComponent {
  component_fields: {
    article_step_data?: ArticleStepData;
    forethought_text: {
      agatha_message: string;
      read_article_text: string;
      read_more_article_text: string;
      read_more_articles_text: string;
      related_articles_text: string;
    };
    knowledge_articles: Array<ArticleComponent>;
    read_article_action?: ReadArticleActions;
    text_message_override?: string;
  };
  component_type: ComponentTypes;
  contextVariables?: ContextVariables;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
}

interface ButtonComponent {
  component_fields: {
    hide_free_form_query?: boolean;
    multi_select: boolean;
    options: Array<ButtonOptions>;
    output_variable: string;
    prompt: string;
    quotedMessageId?: string; // Used by Salesforce One Chat only
    should_confirm_tag?: boolean;
  } & Pick<
    UserMessageComponent['component_fields'],
    | 'agentIcon'
    | 'agentName'
    | 'author'
    | 'should_render_as_markdown'
    | 'should_render_single_new_line_characters'
  >;
  component_type: ComponentTypes;
  contextVariables?: ContextVariables;
  delay?: number;
  hideTimestamp?: boolean;
  isInAgentConvo?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
  timestamp?: number;
}

type Metadata = {
  description: string;
  image: string;
  title: string;
  url: string;
};

type Nullable<T> = { [P in keyof T]: T[P] | null };

export interface FileAttachment {
  content_type?: string | null;
  file_extension?: string | null;
  filesize?: number | null;
  name: string;
  url: string;
}

interface UserMessageComponent {
  component_fields: {
    agentIcon?: string;
    agentName?: string;
    article_step_data?: ArticleStepData;
    articles?: LiveChatArticle[];
    attachments?: FileAttachment[];
    author?: string;
    csat_survey_link?: string;
    csat_survey_text?: string;
    knowledge_articles?: NewIrArticleComponent[];
    message: string;
    metadata?: Nullable<Metadata>;
    read_article_action?: ReadArticleActions;
    should_render_as_markdown?: boolean;
    should_render_single_new_line_characters?: boolean;
    translated_message?: string;
  };
  component_type: ComponentTypes;
  contextVariables?: ContextVariables;
  delay?: number;
  hideTimestamp?: boolean;
  isInAgentConvo?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
  timestamp?: number;
}

interface InteractiveEmailInquiryComponent {
  component_fields: {
    source_body: string;
    source_doc_type: string;
    source_id: string;
    source_title: string;
  };
  component_type: 'interactive_email_inquiry';
}

interface ZendeskHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    chat_tags: string[] | null;
    department: string | null;
    department_with_id: {
      id: string;
      name: string;
    } | null;
    handoff_successful_variable: string;
    hide_zd_widget_delay: number | null;
    ignore_agent_availability: boolean;
    visitor_email: string | null;
    visitor_name: string | null;
    visitor_question: string | null;
  };
}

interface ZendeskMessagingHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    auth_token?: string;
    custom_fields_and_values?: Record<string, string>;
    handoff_successful_variable: string;
    ticket_tags?: string[];
    visitor_question?: string | null;
  };
}

interface ZendeskTalkHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    handoff_successful_variable: undefined;
    jwt_token?: string;
    line_id: string;
    visitor_question: string;
  };
}

export type SipCallEventType = 'call_ended' | 'call_initiated';

interface ZendeskSipCallStepComponent {
  component_fields: {
    room_url: string;
    sip_uri: string;
    token: string;
  };
  component_type: 'zendesk_sip_call';
  contextVariables?: ContextVariables;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
}

interface ZendeskSipCallTriggerStepComponent {
  component_fields: Record<string, never>;
  component_type: 'zendesk_sip_call_trigger';
  contextVariables?: ContextVariables;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
}

interface IntercomAutoSendConfig {
  auto_send_timeout: number;
  should_auto_send_message: boolean;
}

type IntercomChatAutoSendConfig = IntercomAutoSendConfig | null;

interface IntercomChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    auto_send_config: IntercomChatAutoSendConfig;
    handoff_successful_variable: string;
    identity_verification_enabled: boolean;
    visitor_email: string;
    visitor_name: string | null;
    visitor_question: string;
    visitor_user_id: string | null;
  };
}

interface SnapEngageChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    additional_parameters: AdditionalParameters | null;
    handoff_successful_variable: string;
    visitor_email: string | null;
    visitor_name: string | null;
    visitor_question: string | null;
  };
}

interface DriftChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    handoff_successful_variable: string;
    visitor_email: string | null;
    visitor_name: string | null;
    visitor_question: string | null;
  };
}

type BrandEmbassyCustomFields = {
  [key: string]: string;
} | null;

interface BrandEmbassyChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    auto_start_session: boolean;
    custom_fields: BrandEmbassyCustomFields;
    handoff_successful_variable: string;
    visitor_name: string | null;
    visitor_question: string;
  };
}

interface GenericHandoffEventData {
  [key: string]: number | boolean | string;
}

interface GenericChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    event_data: GenericHandoffEventData;
    handoff_successful_variable: string;
    widget_option: GenericHandoffWidgetOption;
  };
}

export type KustomerCustomAttributes = Record<string, unknown>;

interface KustomerHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    assistant_id: string | null;
    custom_fields: KustomerCustomAttributes;
    handoff_successful_variable: string;
    jwt: string | null;
    transcript: string;
    visitor_email: string | null;
    visitor_name: string | null;
    visitor_question: string;
  };
}

// See https://developer.kustomer.com/chat-sdk/v2.0-Web/docs/kustomer-create-conversation#callbackresponse
interface KustomerCallbackResponse {
  conversationId: string;
  createdAt: string;
  ended: boolean;
  isInAssistantMode: boolean;
}

interface AdditionalParameters {
  [key: string]: any;
}

interface SalesforceExtraPrechatFormDetails {
  display_to_agent: boolean;
  label: string;
  overwrite_value_with_transcript: boolean;
  transcript_fields: string[];
  value: string;
}

interface FormattedSalesforceExtraPrechatFormDetails {
  displayToAgent: boolean;
  label: string;
  transcriptFields: string[];
  value: string;
}

interface SalesforceChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    additional_parameters: AdditionalParameters | null;
    extra_prechat_form_details: SalesforceExtraPrechatFormDetails[] | null;
    handoff_successful_variable: string;
    visitor_email: string | null;
    visitor_name: string | null;
    visitor_question: string | null;
  };
}

interface UjetHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    handoff_successful_variable: undefined;
    zendesk_ticket_id: string;
  };
}

interface GorgiasChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    handoff_successful_variable: string;
    question: string;
  };
}

interface TalkdeskChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    email: string | null;
    handoff_successful_variable: string;
    name: string | null;
    prechat_fields: Record<string, string> | null;
    question: string;
    transcript: string;
    transcript_field: string | null;
  };
}

interface LiveChatChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    email: string | null;
    handoff_successful_variable: string;
    name: string | null;
    question: string;
  };
}

type SalesforceVisibleFields = Record<
  string,
  { isEditableByEndUser: boolean; value: string }
>;

interface SalesforceMessagingHandoffStepComponent
  extends BaseWidgetHandoffStep {
  component_fields: {
    clear_session_before_handoff: boolean;
    handoff_successful_variable: string;
    hidden_fields: Record<string, string>;
    question: string;
    visible_fields: SalesforceVisibleFields;
  };
}

interface FreshchatChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    custom_conversation_attributes: Record<string, any> | null;
    email: string | null;
    first_name: string | null;
    handoff_successful_variable: string;
    last_name: string | null;
    question: string;
    tags: string[] | null;
  };
}

interface GladlyChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    handoff_successful_variable: string;
    visitor_email: string;
    visitor_name: string;
    visitor_question: string;
  };
}

interface HubspotChatHandoffStepComponent extends BaseWidgetHandoffStep {
  component_fields: {
    handoff_successful_variable: string;
    user_token: string | null;
    visitor_email: string | null;
    visitor_first_name: string | null;
    visitor_last_name: string | null;
  };
}

interface BaseArticleComponent {
  document_id: string;
  full_html?: string;
  link: string | null;
  quick_feedback_id?: string;
  span_id?: string;
  standardized_document_id?: string;
  title: string;
  trace_id?: string;
}

interface ArticleComponent extends BaseArticleComponent {
  confidence_level?: string;
  confidence_score?: number;
  date_created?: string;
  date_updated?: string;
  preview: string;
  source_type: string;
}

interface NewIrArticleComponent extends BaseArticleComponent {
  full_html: string;
  link: string;
  standardized_article_id: string;
}

interface OpenUrlComponent {
  component_fields: {
    url: string;
  };
  component_type: ComponentTypes;
}

interface ZendeskAttachmentUploadStepComponent {
  component_fields: {
    forethought_text: AttachmentStepText;
    previous_attachment_tokens: string;
    required: boolean;
    should_use_s3?: boolean;
    upload_url: string;
  };
  component_type: ComponentTypes;
  contextVariables?: ContextVariables;
  delay?: number;
  file_names?: Array<string>;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  neverMindSelection?: string;
  query?: string;
}

export interface AttachmentStepText {
  add_file: string;
  form_title: string;
  form_title_required: string;
  never_mind: string;
  skip_attachment: string;
  submit_button: string;
}

interface AnalyzingYourAttachmentsStepText {
  analyzing_your_attachments: string;
  attachments: string;
  this_can_take_some_time: string;
}

interface AttachmentUploadStepComponent {
  component_fields: {
    forethought_text: AttachmentStepText;
    max_individual_size?: number | null;
    max_total_size: number;
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
}

interface AttachmentAnalysisUploadStepComponent {
  component_fields: {
    allow_skip_attachment: boolean;
    forethought_text: AttachmentStepText;
    num_files: number;
    output_variable: string;
    prompt: string;
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
}

interface AnalyzingYourAttachmentsStepComponent {
  component_fields: {
    forethought_text: AnalyzingYourAttachmentsStepText;
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
}

const isAttachmentAnalysisUploadStepComponent = (
  widgetComponent:
    | AttachmentAnalysisUploadStepComponent
    | AttachmentUploadStepComponent
    | ZendeskAttachmentUploadStepComponent,
): widgetComponent is AttachmentAnalysisUploadStepComponent => {
  return widgetComponent.component_type === 'attachment_analysis_upload';
};

interface EventMessageComponent {
  component_fields: {
    message: string;
  };
  component_type: 'event_message';
  contextVariables?: ContextVariables;
  delay?: number;
  isConversationResolver?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  query?: string;
  timestamp?: number;
}

export interface KustomerSatisfactionComponent {
  component_fields: KustomerOnSatisfactionReceived;
  component_type: 'kustomer_satisfaction_survey';
  contextVariables?: ContextVariables;
  delay?: number;
  isConversationResolver?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  query?: string;
  timestamp?: number;
}

export interface BrowserAgentComponent {
  component_fields: {
    channel_id: string;
  };
  component_type: 'browser_agent';
  contextVariables?: ContextVariables;
  delay?: number;
  isConversationResolver?: boolean;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  query?: string;
  timestamp?: number;
}

type WidgetComponent =
  | FormStepComponent
  | ButtonComponent
  | ArticleCarouselComponent
  | UserMessageComponent
  | SelectIntentComponent
  | ZendeskAttachmentUploadStepComponent
  | DynamicCardStepComponent
  | EventMessageComponent
  | ForethoughtLiveChatComponent
  | KustomerSatisfactionComponent
  | ConversationOauthSignInWidgetComponent
  | ZendeskSipCallStepComponent
  | ZendeskSipCallTriggerStepComponent
  | BrowserAgentComponent;

interface InitConversationInterface {
  conversation_id: string;
  next_step_id: string;
  transcript_length: number;
  widget_components: Array<WidgetComponent>;
}

interface ConversationNotFoundError {
  message: string;
  status: 404;
}

interface BadRequestError {
  message: string;
  status: 400;
}

interface TooManyRequestsError {
  message: string;
  status: 429;
}

interface AttemptedToFetchRedactedConversationError {
  message: string;
  status: 400;
}

export const isConversationNotFoundError = (
  data: unknown,
): data is ConversationNotFoundError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 404
  );
};

export const isBadRequestError = (data: unknown): data is BadRequestError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 400 &&
    'message' in data &&
    typeof data.message === 'string' &&
    data.message.startsWith('Invalid request')
  );
};

export const isTooManyRequestsError = (
  data: unknown,
): data is TooManyRequestsError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 429
  );
};

export const isAttemptedToFetchRedactedConversationError = (
  data: unknown,
): data is AttemptedToFetchRedactedConversationError => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 400 &&
    'message' in data &&
    typeof data.message === 'string' &&
    data.message.startsWith('Attempted to fetch redacted conversation with id')
  );
};

export const isNonJsonResponse520Error = (data: unknown): boolean => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'status' in data &&
    data.status === 520 &&
    'message' in data &&
    typeof data.message === 'string' &&
    data.message.startsWith('Non-JSON response:')
  );
};

interface CsatData {
  additional_feedback?: string;
  csat_feedback?: string[];
  csat_resolve?: boolean;
  csat_score?: number;
  helpdesk_conversation_id?: string;
  live_chat_integration?: string;
  submitted: boolean;
}

interface CsatFormData {
  csatFeedback: string[];
  csatResolve?: boolean;
  csatScore: 0 | 1 | 2 | 3 | 4 | 5;
  isCsatDataSubmitted: boolean;
}

interface CsatConfig {
  csatType: 'close' | 'inline';
  didSubmit: boolean;
  isCsatDetailVisible: boolean;
  isCsatEndTransition?: boolean;
  isCsatSurveyFinished?: boolean;
  isCsatVisible?: boolean;
}

interface CsatStates {
  actionToExecuteAfterCsatDismiss:
    | 'end-live-chat'
    | 'resume-from-trigger-point'
    | null;
  additionalFeedback: string;
  currentStage: number;
  hasDismissed: boolean;
  hasFeedbackTooltipPrompted: boolean;
  hasSubmitted: boolean;
  highRatingReasonIndices: number[];
  isCsatVisible: boolean;
  isEditing: boolean;
  isPromptDisabledForMinimize: boolean;
  isResolved?: boolean;
  lowRatingReasonIndices: number[];
  rating: number;
  submittedRating?: number;
  triggeredFrom: WidgetCsatOpenedTrackingEvent['triggered_from'];
}

interface SipCallState {
  audioAccess: boolean | null;
  isVisible: boolean;
  readyToCall?: boolean;
  roomUrl?: string;
  sipUri?: string;
  token?: string;
}

type CsatTranslatedText = {
  text: Partial<{
    CSAT_BAD: string;
    CSAT_CHANGE_RATING: string;
    CSAT_CLOSE: string;
    CSAT_DID_NOT_UNDERSTAND: string;
    CSAT_EASY: string;
    CSAT_EXCELLENT: string;
    CSAT_FRIENDLY_TONE: string;
    CSAT_GOOD: string;
    CSAT_GOOD_SOLUTION: string;
    CSAT_IMPERSONAL: string;
    CSAT_OKAY: string;
    CSAT_PERSONALIZED: string;
    CSAT_QUICK_TURNAROUND: string;
    CSAT_RATING_QUESTION: string;
    CSAT_RESOLVE_QUESTION: string;
    CSAT_SKIP_AND_CLOSE: string;
    CSAT_SUBMIT: string;
    CSAT_TELL_US_MORE: string;
    CSAT_TERRIBLE: string;
    CSAT_THANK_YOU: string;
    CSAT_TOOK_TOO_LONG: string;
    CSAT_UNDERSTOOD_NEEDS: string;
    CSAT_UNFRIENDLY: string;
    CSAT_UNHELPFUL: string;
    CSAT_UNHELPFUL_RESPONSE: string;
    CSAT_YOU_RATED: string;
    FT_NO: string;
    FT_YES: string;
  }>;
};

type ApiHeaders = {
  Authorization: string;
  'is-config-preview': boolean;
  'is-draft': boolean;
  'is-preview': boolean;
  'solve-origin': string;
  'workflow-config-version'?: string;
};

interface DropdownItem {
  //dropdown options
  dropdown_options?: Array<DropdownItem>;
  //label for the dropdown option
  label: string;
  //value for the dropdown option
  value: string;
}

interface SendTrackingActionCommonArgs {
  channel: 'live' | 'preview';
  conversationId?: string;
  event_type: TrackingEventTypes;
  product: 'flamethrower';
  sessionId?: string;
}

interface BaseTrackingEvent {
  event_type: TrackingEventTypes;
}

interface WidgetOpenedTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-opened';
  mechanism:
    | 'manual-click-on-widget-icon'
    | 'manual-click-on-proactive-greeting'
    | 'manual-click-on-proactive-intent-button'
    | 'js-api-widget-open'
    | 'js-api-widget-launchQuery'
    | 'full-screen-true'
    | 'proactive-trigger';
}

interface WidgetPlainTrackingEvent {
  event_type: PlainTrackingEventTypes;
}

interface WidgetCsatSurveyTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-csat-submitted';
  triggered_from: WidgetCsatOpenedTrackingEvent['triggered_from'];
}

interface WidgetCsatOpenedTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-csat-opened';
  triggered_from:
    | 'minimize'
    | 'csat-trigger-point'
    | 'end-live-chat'
    | 'header-button-clicked'
    | 'zopim-chat-rating-request'
    | null;
}

interface WidgetErrorBannerVisibleTrackingEvent extends BaseTrackingEvent {
  conversation_id: string;
  error_message: string;
  event_type: 'widget-error-banner-visible';
  is_preview: boolean;
  method?: string;
  response_data?: unknown;
  status?: number;
  url?: string;
}

interface ZopimAgentSessionMismatch extends BaseTrackingEvent {
  conversation_id: string;
  description: string;
  event_type: 'zopim-agent-session-mismatch';
  is_preview: boolean;
}

interface GoToArticleTrackingEvent extends BaseTrackingEvent {
  current_step_id?: string;
  doc_id: string;
  doc_index: number;
  event_type: 'go-to-article';
  intent_workflow_id?: string;
  intent_workflow_version?: number;
  org_id?: number;
  span_id?: string;
  standardized_document_id?: string;
  trace_id?: string;
}

interface WidgetCustomerEnteredIntentByProactiveBubbleTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-entered-intent-by-proactive-bubble';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerEnteredIntentByButtonTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-entered-intent-by-button';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerAgentChatTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-customer-agent-chat';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerArticleSuggestionHelpedTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-article-suggestion-helped';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerArticleSuggestionUnhelpfulTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-customer-article-suggestion-unhelpful';
  intent_id?: string;
  workflow_id?: string;
}

interface WidgetCustomerHandoffTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-customer-handoff';
}

type WidgetCustomerHandoffTrackingEventWithData =
  | (WidgetCustomerHandoffTrackingEvent & HandoffData)
  | (WidgetCustomerHandoffTrackingEvent &
      HandoffData & {
        // `handoffData` subobject is deprecated, keeping it for backward
        // compatibility:
        handoffData: {
          conversation_id: string;
          integration: 'sunco-live-chat';
          success: boolean;
        };
      });

interface WidgetZendeskUpdateSettingsTrackingEvent
  extends BaseTrackingEvent,
    Record<string, unknown> {
  event_type: 'widget-zendesk-update-settings';
}

interface WidgetZendeskAttachmentUploadTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-zendesk-attachment-upload';
  failedReason?: string;
  successfulUploads: number;
}

interface WidgetDisabledByLocalStorageTrackingEvent extends BaseTrackingEvent {
  current_timestamp: number;
  disabled_until_timestamp: number;
  event_type: 'widget-disabled-by-local-storage';
  original_conversation_id: string;
}

interface WidgetHandoffAuthenticationFailureTrackingEvent
  extends BaseTrackingEvent {
  error?: string;
  event_type: 'widget-handoff-auth-failure';
  integration: HandoffIntegrations;
}

interface WidgetHandoffAuthenticationSuccessTrackingEvent
  extends BaseTrackingEvent {
  event_type: 'widget-handoff-auth-success';
  integration: HandoffIntegrations;
}

interface WidgetLiveChatSendMessageTrackingEvent extends BaseTrackingEvent {
  chat_id: string;
  conversation_id: string;
  event_type: 'widget-live-chat-send-message';
  is_sdk_initialized: boolean;
}

interface WidgetStartHandoffTrackingEvent extends BaseTrackingEvent {
  chat_id?: string;
  conversation_id: string;
  event_type: 'widget-start-handoff';
  integration: HandoffIntegrations;
  live_chat_status?: LiveChatStatus;
}

interface WidgetSmoochTrackingEvent extends BaseTrackingEvent {
  description: string;
  event_type: 'widget-smooch-event';
  integration: 'sunco-live-chat';
}

interface WidgetZendeskTrackingEvent extends BaseTrackingEvent {
  description: string;
  event_type: 'widget-zendesk-event';
}

interface WidgetZopimTrackingEvent extends BaseTrackingEvent {
  description: string;
  event_type: 'widget-zopim-event';
  integration: 'zendesk-zopim-live-chat';
}

interface WidgetSalesforceTrackingEvent extends BaseTrackingEvent {
  description: string;
  event_type: 'widget-salesforce-event';
  integration: 'salesforce';
}

interface WidgetLiveChatTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-live-chat-event';
  integration: HandoffIntegrations;
}

interface WidgetHandoffTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-handoff-event';
  integration: HandoffIntegrations;
}

export type ClearConversationReason =
  | 'restart_in_livechat_mode'
  | 'reset_widget_on_restart'
  | 'successful_handoff_on_submit'
  | 'redacted_conversation'
  | 'end_live_chat'
  | 'clear_local_data'
  | 'launch_query'
  | 'clear_conversation_on_timeout';

interface WidgetConversationClearedTrackingEvent extends BaseTrackingEvent {
  event_type: 'widget-conversation-cleared';
  reason: ClearConversationReason;
}

interface ProactiveTriggerTrackingEvent {
  event_type: 'proactive-trigger';
  params?: Record<string, string>;
  proactive_trigger_id: string;
  trigger_type: AutomationForethoughtEvents;
}

export type SendTrackingEventParameters =
  | WidgetCsatSurveyTrackingEvent
  | WidgetOpenedTrackingEvent
  | GoToArticleTrackingEvent
  | WidgetCustomerEnteredIntentByProactiveBubbleTrackingEvent
  | WidgetCustomerEnteredIntentByButtonTrackingEvent
  | WidgetCustomerAgentChatTrackingEvent
  | WidgetCustomerArticleSuggestionHelpedTrackingEvent
  | WidgetCustomerArticleSuggestionUnhelpfulTrackingEvent
  | WidgetCustomerHandoffTrackingEventWithData
  | WidgetZendeskUpdateSettingsTrackingEvent
  | WidgetZendeskAttachmentUploadTrackingEvent
  | WidgetDisabledByLocalStorageTrackingEvent
  | WidgetCsatOpenedTrackingEvent
  | WidgetHandoffAuthenticationFailureTrackingEvent
  | WidgetErrorBannerVisibleTrackingEvent
  | ZopimAgentSessionMismatch
  | WidgetPlainTrackingEvent
  | WidgetLiveChatSendMessageTrackingEvent
  | WidgetStartHandoffTrackingEvent
  | WidgetSmoochTrackingEvent
  | WidgetHandoffAuthenticationSuccessTrackingEvent
  | WidgetLiveChatTrackingEvent
  | WidgetHandoffTrackingEvent
  | WidgetZopimTrackingEvent
  | WidgetConversationClearedTrackingEvent
  | ProactiveTriggerTrackingEvent
  | WidgetSalesforceTrackingEvent
  | WidgetZendeskTrackingEvent;

export type SendTrackingActionArgs = SendTrackingActionCommonArgs &
  SendTrackingEventParameters;

export interface ResetConversationInterface {
  acknowledged: boolean;
  modified_date?: string;
  next_step_id: string;
  transcript_length: number;
}

interface SelectIntentComponent {
  component_fields: {
    custom_greeting?: string;
    language?: string;
    show_greeting_message: boolean;
    show_top_n_intents: boolean;
    translated_greeting_line?: string;
  };
  component_type: ComponentTypes;
  // Adding these for parity with the other component expected types from WidgetComponents to resolve type issues in the reducer. Once we split the reducer into v1 and v2 we can remove these.
  contextVariables?: ContextVariables;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  language?: string;
  query?: string;
}

interface InitIntentInterface {
  // Key should be either intent_id or free_form_query.
  [key: string]: string;
}

interface IntentConversationResponse {
  conversation_id: string;
  intent_id?: string;
  is_csat_promptable?: boolean;
  language?: string | null;
  modified_date?: string;
  next_step_id: string | null;
  preview_logs?: PreviewLog[];
  previous_intent_ids?: Array<string> | null;
  redacted_text?: string;
  transcript_length: number | null;
  user_interactions?: number | null;
  widget_components?: Array<WidgetComponent>;
  workflow_id?: string;
}

interface SubmitInteractiveEmailDeflectionFeedbackResponse {
  redirect_url: string;
}

interface CsatFeedbackRequestFields {
  additional_feedback?: string;
  csat_feedback?: string[];
  csat_resolve?: boolean;
  csat_score?: number;
  csat_trigger_point_prompted?: boolean;
  is_partial_submission: boolean;
  resume_from_trigger_point: boolean;
  routing_intent_workflow_id?: string;
}

function isCsatFeedbackRequestFields(
  fields: CsatFeedbackRequestFields | QuickFeedbackRequestFields,
): fields is CsatFeedbackRequestFields {
  return (
    (fields as CsatFeedbackRequestFields).is_partial_submission !== undefined
  );
}

interface QuickFeedbackRequestFields {
  additionalFeedback: string;
  articleStepData: ArticleStepData | undefined;
  isUseful: boolean;
  knowledgeArticles: BaseArticleComponent[];
  textMessageOverride: string | undefined;
}

interface StreamingResponseKR {
  modified_date?: string | null;
  next_step_id?: string | null;
  transcript_length?: number | null;
  type: 'knowledge_retrieval_stream';
  widget_components: Array<WidgetComponent>;
}

interface StreamingResponsePreviewLog {
  conversation_id: string;
  preview_logs: PreviewLog[];
  type: 'preview_logs';
}

const isPreviewLogStreamingResponse = (
  response: StreamingResponse,
): response is StreamingResponsePreviewLog => {
  return response.type === 'preview_logs';
};

type StreamingResponse = StreamingResponseKR | StreamingResponsePreviewLog;

type WidgetConfigRequest = {
  conversation_id: string | undefined;
  parent_url?: string;
  raw_params?: {
    [attrName: string]: string | boolean;
  };
};

type PresignedUrlRequest = {
  content_length: number;
  conversation_id: string | undefined;
  file_name: string;
  should_generate_download_link: boolean;
};

type IntentFilters = Record<
  string,
  { is_autoflow: boolean; is_draft: boolean }
>;

type AiInstructionFilters = { ai_instruction_id: string; is_draft: boolean }[];
type ContentSnippetFilters = { content_snippet_id: string }[];

type SendFreeFormQueryRequest = {
  ai_instruction_filters_for_preview?: AiInstructionFilters[] | null;
  component_type?: ComponentTypes;
  content_snippet_filters_for_preview?: ContentSnippetFilters[] | null;
  context_variables?: ContextVariables;
  intent_filters_for_preview?: IntentFilters | null;
  next_step_id: string | null;
  query: string;
  template_intent_id?: string | null;
  transcript_length: number | null;
};

type RestartConversationRequest = {
  context_variables: ContextVariables;
  next_step_id: string | null;
  transcript_length: number | null;
};

type RestartIntentConversationRequest = {
  context_variables: ContextVariables;
  keep_previous_intent_ids?: boolean;
  next_step_id: string | null;
  transcript_length: number | null;
};

type UpdateIntentConversationRequest = {
  ai_instruction_filters_for_preview?: AiInstructionFilters[] | null;
  content_snippet_filters_for_preview?: ContentSnippetFilters[] | null;
  context_variables: ContextVariables;
  intent_filters_for_preview?: IntentFilters | null;
  next_step_id: string | null;
  transcript_length: number | null;
};

type SendFeedbackRequest = {
  csat_feedback_fields?: SendCsatFeedbackFields;
  feedback_fields?: SendQuickFeedbackRequest;
};

type SendCsatFeedbackFields = {
  additional_feedback?: string;
  csat_feedback?: string[];
  csat_resolve?: boolean;
  csat_score?: number;
  csat_trigger_point_prompted?: boolean;
  is_partial_submission: boolean;
  resume_from_trigger_point: boolean;
  routing_intent_workflow_id?: string;
};

type SendQuickFeedbackRequest = {
  additional_feedback: string;
  article_step_data: ArticleStepData | undefined;
  is_useful: boolean;

  // Force implementation code to set `full_html: undefined` to avoid sending
  // it to the backend:
  knowledge_articles: (BaseArticleComponent & { full_html: undefined })[];

  text_message_override: string | undefined;
};

type InitIntentConversationRequest = {
  ai_instruction_filters_for_preview?: AiInstructionFilters[] | null;
  content_snippet_filters_for_preview?: ContentSnippetFilters[] | null;
  context_variables: ContextVariables;
  conversation_id?: string;
  from_initial_intent_id?: boolean | null;
  intent_filters_for_preview?: IntentFilters | null;
  next_step_id?: string | null;
  origin_information?: {
    browser_name: string;
    device_type: string;
    origin: string;
    os: string;
    // mobile_app_id: string;
    parent_url: string;
    solve_ui_type: 'sdk_webview' | 'web' | 'sdk_native';
    user_agent: string;
  };
  previous_intent_ids?: Array<string>;
  solve_widget_product?: string | null;
  transcript_length?: number | null;
  widget_parameters: ConfigState['rawParams'];
};

type HyperlinkRedirectStepComponent = {
  component_fields: {
    destination_link: string;
    forethought_text: {
      click_here: string;
      redirect_message: string;
      redirecting: string;
    };
    redirect_option: 'current_tab' | 'new_tab';
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
};

type OpsgenieAlert = 'trigger-event-step';

type OpsgeniePriority = 'P1' | 'P2' | 'P3' | 'P4' | 'P5';

type OpsgenieTeams = 'Customer NOC Alerts' | 'Solve';

type SendConversationAlertRequest = {
  alert_type: OpsgenieAlert;
  description: string;
  priority: OpsgeniePriority;
  teams: OpsgenieTeams[];
};

export const isButtonsComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ButtonComponent =>
  widgetComponent.component_type === 'buttons';

const isFormComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is FormStepComponent =>
  widgetComponent.component_type === 'form';

const isTextMessageComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is UserMessageComponent =>
  widgetComponent.component_type === 'text_message';

const isWorkflowTagSelectorComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ButtonComponent => {
  const componentFields =
    widgetComponent.component_fields as ButtonComponent['component_fields'];
  return (
    (isButtonsComponent(widgetComponent) &&
      componentFields.should_confirm_tag) ??
    false
  );
};

const isSelectIntentComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is SelectIntentComponent =>
  widgetComponent.component_type === 'restart_conversation';

const isZendeskAttachmentUploadStepComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ZendeskAttachmentUploadStepComponent =>
  widgetComponent.component_type === 'zendesk_attachment_upload';

const isArticleComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ArticleCarouselComponent =>
  widgetComponent.component_type === 'article' ||
  widgetComponent.component_type === 'article_suggestion';

const isEventMessageComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is EventMessageComponent =>
  widgetComponent.component_type === 'event_message';

const isUserMessageComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is UserMessageComponent =>
  widgetComponent.component_type === 'user_message';

const isForethoughtLiveChatHandoffComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ForethoughtLiveChatHandoffComponent =>
  widgetComponent.component_type === 'forethought_live_chat_handoff';

const isSuncoLiveChatComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is SuncoLiveChatComponent =>
  widgetComponent.component_type === 'sunco_live_chat';

const isButtonStepWithHideFreeFormQueryField = (
  widgetComponent: WidgetComponent,
): widgetComponent is ButtonComponent => {
  const componentFields =
    widgetComponent.component_fields as ButtonComponent['component_fields'];

  return (
    (isButtonsComponent(widgetComponent) &&
      componentFields.hide_free_form_query) ??
    false
  );
};

const isOauthSignInComponent = (
  widgetComponent: WidgetComponent,
): widgetComponent is ConversationOauthSignInWidgetComponent => {
  return widgetComponent.component_type === 'conversation_oauth_sign_in';
};

interface AutonomousAgentConversationResponse {
  intent_id: string;
  intent_title: string;
  internal_output: string[];
  is_workflow_preview: boolean;
  policy: string;
  widget_components: WidgetComponent[];
}

interface InitializeAutonomousAgentConversationRequest {
  conversation_id: string;
  free_form_query: string;
  intent_id?: string | null;
}

interface AutonomousAgentQueryRequest {
  intent_id: string;
  is_workflow_preview: boolean;
  query: string;
}

interface ListOption {
  label: string;
  value: string;
}

type TriggerEventStepContextVariable = {
  default: string | boolean | number;
  id: string;
  name: string;
  type: string;
};

type TriggerEventStepComponent = {
  component_fields: {
    additional_context: TriggerEventStepAdditionalContext;
    context_variables_to_update: TriggerEventStepContextVariable[];
    event_id: string;
    event_name: string;
  };
  component_type: ComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
};

interface TriggerEventStepAdditionalContext extends ContextVariables {
  free_form_query: string;
}

interface BaseWidgetHandoffStep {
  component_type: HandoffComponentTypes;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
}

interface ImageStepComponent {
  component_fields: {
    images: {
      description: string;
      url: string;
    }[];
  };
  component_type: 'image';
}

interface Card {
  displayable_dynamic_sections: DisplayableDynamicSection[];
  value: string;
}

interface DisplayableDynamicSection {
  components: Component[];
  type: 'image' | 'content' | 'title';
}

interface Component {
  label?: string;
  value: string;
}

interface DynamicCardStepComponent {
  component_fields: {
    cards: Card[];
    content_config: { limit: number } | null;
    forethought_text: {
      next: string;
      previous: string;
      select: string;
      view_less: string;
      view_more: string;
    };
    message: string | null;
    output_variable: string;
    style: 'carousel' | 'stacked-cards';
  };
  component_type: 'dynamic_card';
  contextVariables?: ContextVariables;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  query?: string;
}

interface EmbedStepComponent {
  component_fields: {
    embed_url: string;
  };
  component_type: 'embed';
}

interface ForethoughtLiveChatComponent {
  component_fields: Record<string, unknown>;
  component_type: 'forethought_live_chat_handoff' | 'sunco_live_chat';
  contextVariables?: ContextVariables;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  query?: string;
}

type LiveChatIntegration =
  | 'intercom'
  | 'sunco'
  | 'freshchat'
  | 'zendesk'
  | 'kustomer'
  | 'salesforce';

export type LiveChatStatus =
  | 'pending'
  | 'active'
  | 'inactive'
  | 'failure'
  | 'ended'
  | undefined;

type PusherLiveChatIntegration = 'intercom' | 'freshchat' | 'sunco';

const pusherLiveChatIntegrations: PusherLiveChatIntegration[] = [
  'intercom',
  'freshchat',
  'sunco',
];

const isPusherLiveChatIntegration = (
  liveChatIntegration?: LiveChatIntegration,
): liveChatIntegration is PusherLiveChatIntegration => {
  return pusherLiveChatIntegrations.includes(
    liveChatIntegration as PusherLiveChatIntegration,
  );
};

interface ForethoughtLiveChatHandoffComponent
  extends ForethoughtLiveChatComponent {
  component_fields: {
    available_agents: {
      avatar: string | null;
      name: string;
    }[];
    customer_facing_transcript: string;
    forethought_text: {
      agent_connected: string;
      chatting_with_agent: string;
      waiting_for_agent: string;
    };
    helpdesk_conversation_id: string;
    integration_config: LiveChatIntegrationConfig;
    placeholder_message: string;
  };
  component_type: 'forethought_live_chat_handoff';
}

interface BaseLiveChatIntegrationConfig {
  integration: LiveChatIntegration;
}

interface ZendeskLiveChatIntegrationConfig
  extends BaseLiveChatIntegrationConfig {
  integration: 'zendesk';
  integration_fields: ZendeskHandoffStepComponent['component_fields'] & {
    account_key: string;
  };
}

interface SalesforceMessagingLiveChatIntegrationConfig
  extends BaseLiveChatIntegrationConfig {
  integration: 'salesforce';
  integration_fields: SalesforceMessagingHandoffStepComponent['component_fields'] & {
    developer_name: string;
    jwt: string | null;
    one_chat: boolean;
    organization_id: string;
    scrt_url: string;
  };
  integration_identifier: 'salesforce_messaging_handoff';
}

interface IntercomLiveChatIntegrationConfig
  extends BaseLiveChatIntegrationConfig {
  integration: 'intercom';
}

interface FreshchatLiveChatIntegrationConfig
  extends BaseLiveChatIntegrationConfig {
  integration: 'freshchat';
}

export interface KustomerLiveChatIntegrationConfig
  extends BaseLiveChatIntegrationConfig {
  integration: 'kustomer';
  integration_fields: KustomerHandoffStepComponent['component_fields'] & {
    api_key: string;
    brand_id: string;
  };
}

type LiveChatIntegrationConfig =
  | ZendeskLiveChatIntegrationConfig
  | IntercomLiveChatIntegrationConfig
  | FreshchatLiveChatIntegrationConfig
  | KustomerLiveChatIntegrationConfig
  | SalesforceMessagingLiveChatIntegrationConfig;

interface CsatTriggerPointStepComponent {
  component_fields: {
    delay: number;
    negative_rating_routing_intent?: string;
    should_use_delay: boolean;
  };
  component_type: 'csat_trigger_point';
  isSent?: boolean;
}

interface SuncoLiveChatComponent extends ForethoughtLiveChatComponent {
  component_fields: {
    app_id: string;
    forethought_text: {
      agent_connected: string;
      chatting_with_agent: string;
      waiting_for_agent: string;
    };
    jwt: string | null;
    placeholder_message: string;
    sunshine_conversation_id?: string;
    web_messenger_integration_id: string;
  };
  component_type: 'sunco_live_chat';
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
}

type HandoffStepComponent =
  | ZendeskHandoffStepComponent
  | ZendeskMessagingHandoffStepComponent
  | SalesforceChatHandoffStepComponent
  | BrandEmbassyChatHandoffStepComponent
  | DriftChatHandoffStepComponent
  | IntercomChatHandoffStepComponent
  | KustomerHandoffStepComponent
  | GenericChatHandoffStepComponent
  | UjetHandoffStepComponent
  | ZendeskTalkHandoffStepComponent;

// preview logs related types
type PreviewLog =
  | ArticleSuggestionStepPreviewLog
  | ApiStepPreviewLog
  | IntentPredictionPreviewLog
  | WarningPreviewLog
  | GotoStepPreviewLog;

interface ArticleSuggestionStepPreviewLog {
  articles_predicted: string[];
  confidence_scores: number[];
  log_type: 'article_suggestion_step';
}

interface WarningPreviewLog {
  log_type: 'warning';
  message?: string;
  title: string;
}

interface ApiStepPreviewLog {
  action_name?: string;
  input_params: Record<string, string>;
  log_type: 'api_step';
  output_params: Record<string, string>;
  url_used?: string;
}

interface IntentPredictionPreviewLog {
  confidence_scores: number[];
  intents_predicted: string[];
  log_type: 'intent_prediction';
}
interface GotoStepPreviewLog {
  intent_id: string;
  intent_title: string;
  log_type: 'goto_step';
}

type WidgetMode = 'workflow' | 'liveChat';

interface LiveChatSendMessageRequest {
  attachments: FileAttachment[];
  message: string;
}

interface LiveChatSendMessageResponse {
  timestamp: string;
  user_reply: string;
}

interface LiveChatSendAgentMessageResponse {
  agent_reply: string;
  timestamp: string;
}

interface SubmitInteractiveEmailDeflectionFeedbackRequest {
  is_deflected: boolean;
  token: string;
}

interface UpdateHelpdeskConversationRequest {
  helpdesk_conversation_id: string;
  include_attachments: boolean | null;
  include_transcript: boolean | null;
  integration: string;
  step_type?: ComponentTypes;
}

interface LiveChatAgentMessageEventData {
  agent: string;
  articles?: LiveChatArticle[];
  attachments?: FileAttachment[];
  avatar: string | undefined;
  message: string;
  type: 'message' | 'event';
}

interface LiveChatArticle {
  link: string;
  preview: string;
  title: string;
}

interface BaseHandoffData {
  error?: string;
  integration: HandoffIntegrations;
  page_url?: string;
  success: boolean;
}

interface ZendeskMessagingHandoffData extends BaseHandoffData {
  browserLanguage: string;
  customFields: Array<{ id: string; value: string }>;
  integration: 'zendesk_messaging';
  question: string;
  ticketTags: string[];
}

interface ZendeskTalkHandoffData extends BaseHandoffData {
  integration: 'zendesk_talk';
  question: string;
}

interface GladlyHandoffData extends BaseHandoffData {
  question: string;
  widgetAvailability: string;
}

interface ZendeskHandoffData extends BaseHandoffData {
  chatTags: string[];
  departmentReceived: string;
  departmentReceivedName: string;
  departments?: ZendeskDepartments | null;
  email: string;
  ignoreAgentAvailability: boolean;
  integration: 'zendesk';
  isReceivedDepartmentOnline?: boolean;
  name: string;
  question: string;
}

interface SalesforceHandoffData extends BaseHandoffData {
  integration: 'salesforce';
  manuallyClickSalesforceButton: boolean;
}

interface KustomerHandoffData extends BaseHandoffData {
  customFields: AdditionalParameters;
  integration: 'kustomer';
  question: string;
  widgetAvailability?: 'disabled' | 'offline' | 'online' | 'hidden' | 'none';
}

interface IntercomHandoffData extends BaseHandoffData {
  autoSendConfig: IntercomChatAutoSendConfig;
  email: string;
  identityVerificationEnabled: boolean;
  integration: 'intercom';
  name: string;
  question: string;
  userid: string;
}

interface SnapEngageHandoffData extends BaseHandoffData {
  additionalParameters: AdditionalParameters;
  email: string;
  integration: 'snap_engage';
  name: string;
  question: string;
}

interface DriftHandoffData extends BaseHandoffData {
  email: string;
  integration: 'drift';
  name: string;
  question: string;
}

interface GenericHandoffData extends BaseHandoffData {
  integration: 'generic';
  widgetOption: GenericHandoffWidgetOption;
}

interface BrandEmbassyHandoffData extends BaseHandoffData {
  autoStartSession: boolean;
  customFields: BrandEmbassyCustomFields;
  integration: 'brand_embassy';
  name: string;
  question: string;
}

interface GorgiasHandoffData extends BaseHandoffData {
  integration: 'gorgias';
  question: string;
}

interface TalkdeskHandoffData extends BaseHandoffData {
  integration: 'talkdesk';
  question: string;
}

interface FreshchatHandoffData extends BaseHandoffData {
  conversationProperties: Record<string, unknown> | null;
  email: string;
  firstName: string;
  forethoughtConversationID: string | null;
  integration: 'freshchat';
  lastName: string;
  question: string;
  tags: string[] | null;
}

interface SalesforceMessagingHandoffData extends BaseHandoffData {
  clearSessionBeforeHandoff: boolean;
  hiddenFields: Record<string, string>;
  integration: 'salesforce_messaging';
  question: string;
  visibleFields: SalesforceVisibleFields;
}

interface LiveChatMessagingHandoffData extends BaseHandoffData {
  email: string;
  integration: 'live_chat';
  name: string;
  question: string;
}

interface SuncoLiveChatHandoffData extends BaseHandoffData {
  conversation_id?: string;
  integration: 'sunco-live-chat';
  jwt: string | null;
  question?: string;
  smooch_app_user_id?: string;
  smooch_client_id?: string;
  smooch_session_token?: string;
}

interface UnknownHandoffData extends BaseHandoffData {
  question: string;
}

interface ZendeskLiveChatHandoffData extends BaseHandoffData {
  conversation_id: string;
  department_id?: number;
  display_name: string | null;
  email: string | null;
  integration: 'zendesk-zopim-live-chat';
  tags: string[];
}

interface PusherLiveChatHandoffData extends BaseHandoffData {
  conversation_id: string;
  helpdesk_conversation_id: string;
  integration: `${PusherLiveChatIntegration}-live-chat`;
}

interface KustomerLiveChatHandoffData extends BaseHandoffData {
  conversation_id: string;
  helpdesk_conversation_id: string;
  integration: 'kustomer-live-chat';
}

interface SalesforceMessagingLiveChatHandoffData extends BaseHandoffData {
  clearSessionBeforeHandoff: boolean;
  conversation_id: string;
  helpdesk_conversation_id: string;
  hiddenFields: Record<string, string>;
  integration: 'salesforce';
  question: string;
  visibleFields: SalesforceVisibleFields;
}

export interface PersistedWidgetComponent {
  component_attributes: Record<string, unknown>;
  component_fields: Record<string, unknown>;
  component_type: ComponentTypes;
}
interface WidgetComponentsResponse {
  widget_components: Array<PersistedWidgetComponent>;
}

export interface KustomerOneChatOnLoadConfig {
  brand_id: string;
  enabled: boolean;
  jwt: string;
  key: string;
}

export interface OnLoadConfig {
  kustomer_one_chat: KustomerOneChatOnLoadConfig | null;
}

type AutomationForethoughtEvents =
  | 'open-widget'
  | 'trigger-intent'
  | 'show-proactive-prompt';

interface TriggerEvent {
  delay_ms?: number;
  params?: Record<string, unknown>;
  type: AutomationForethoughtEvents;
}

export interface ExternalEventAutomation {
  events_to_trigger: TriggerEvent[];
  external_event_automation_id: string;
  external_event_name: string;
}

export interface ProactiveTrigger {
  events_to_trigger: TriggerEvent[];
  name: string;
  proactive_trigger_id: string;
}

interface Variant {
  available_for_sale?: boolean;
  details_page_url: string;
  id: string;
  image_url?: string;
  is_add_to_cart_enabled?: boolean;
  original_price?: string;
  price: string;
  title: string;
}

interface Product {
  description: string;
  id: string;
  image_url?: string;
  title: string;
  variants: Variant[];
}

interface ProductRecommendationStepComponent {
  component_fields: {
    products: Product[];
  };
  component_type: 'product_recommendation';
  contextVariables?: ContextVariables;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  query?: string;
}

export interface KnowledgeResult {
  confidence_level: string;
  confidence_score: number;
  date_created: string;
  date_updated: string;
  document_id: string;
  full_html?: string;
  link?: string;
  preview: string;
  source_type: string;
  standardized_document_id?: string;
  title: string;
}

export interface KnowledgeSearchRequest {
  query: string;
  widget_parameters: ConfigState['rawParams'];
}

export interface KnowledgeSearchClickRequest {
  document_id: string;
  document_link: string;
  document_title: string;
  query: string;
}

export interface KnowledgeSearchResponse {
  conversation_id: string;
  query: string;
  results: KnowledgeResult[];
}

export type WidgetArticleSearchOrigin = 'floating_bar' | 'search_page';

type HandoffData =
  | ZendeskMessagingHandoffData
  | ZendeskHandoffData
  | SalesforceHandoffData
  | KustomerHandoffData
  | IntercomHandoffData
  | SnapEngageHandoffData
  | DriftHandoffData
  | GenericHandoffData
  | BrandEmbassyHandoffData
  | GorgiasHandoffData
  | TalkdeskHandoffData
  | FreshchatHandoffData
  | SalesforceMessagingHandoffData
  | LiveChatMessagingHandoffData
  | SuncoLiveChatHandoffData
  | UnknownHandoffData
  | ZendeskLiveChatHandoffData
  | PusherLiveChatHandoffData
  | KustomerLiveChatHandoffData
  | SalesforceMessagingLiveChatHandoffData
  | ZendeskTalkHandoffData
  | GladlyHandoffData;

interface UpdateForethoughtLiveChatHelpdeskConversationIdResponse {
  helpdesk_conversation_id: string;
  timestamp: string;
}

type ConversationOauthCredentialStatus =
  | 'active'
  | 'inactive'
  | 'pending'
  | 'error';

export interface ConversationOauthSignInWidgetComponent {
  component_fields: {
    auth_uri: string;
    client_name: string;
    conversation_oauth_credential_id: string;
    conversation_oauth_credential_status: ConversationOauthCredentialStatus;
    disable_other_inputs: boolean;
    error_message: string;
    prompt: string;
    sign_in_button_label: string;
    success_message: string;
  };
  component_type: 'conversation_oauth_sign_in';
  contextVariables?: ContextVariables;
  delay?: number;
  isReroutedByNegativeFeedback?: boolean;
  isSent?: boolean;
  isStreamingComponent?: boolean;
  query: string;
}

export interface ConversationOauthCredentialStatusResponse {
  conversation_id: string;
  conversation_oauth_credential_id: string;
  status: ConversationOauthCredentialStatus;
}

export interface HandleSuncoMultiConvoDisabledRequest {
  conversation_id: string;
  sunco_conversation_id: string;
}

export type {
  LiveChatSendMessageResponse,
  SubmitInteractiveEmailDeflectionFeedbackRequest,
  IntentFilters,
  AnalyzingYourAttachmentsStepComponent,
  AttachmentAnalysisUploadStepComponent,
  AutonomousAgentQueryRequest,
  AutonomousAgentConversationResponse,
  InitializeAutonomousAgentConversationRequest,
  InitIntentInterface,
  ArticleComponent,
  NewIrArticleComponent,
  PreviewLog,
  InitConversationInterface,
  FormFields,
  FormStepComponent,
  FormStepComponentText,
  DynamicCardStepComponent,
  ButtonComponent,
  ButtonOptions,
  CsatTriggerPointStepComponent,
  UserMessageComponent,
  InteractiveEmailInquiryComponent,
  ArticleCarouselComponent,
  ContextVariables,
  WorkflowConfigArgs,
  WorkflowConfigResponse,
  WidgetComponent,
  CsatConfig,
  CsatStates,
  SipCallState,
  CsatData,
  CsatFormData,
  CsatTranslatedText,
  ZendeskHandoffStepComponent,
  UjetHandoffStepComponent,
  SalesforceChatHandoffStepComponent,
  FormattedSalesforceExtraPrechatFormDetails,
  OpenUrlComponent,
  ZendeskMessagingHandoffStepComponent,
  ZendeskTalkHandoffStepComponent,
  KustomerHandoffStepComponent,
  ApiHeaders,
  ZendeskAttachmentUploadStepComponent,
  AttachmentUploadStepComponent,
  DropdownItem,
  AdditionalParameters,
  IntercomChatAutoSendConfig,
  IntercomChatHandoffStepComponent,
  SnapEngageChatHandoffStepComponent,
  DriftChatHandoffStepComponent,
  GenericChatHandoffStepComponent,
  GenericHandoffEventData,
  SelectIntentComponent,
  BrandEmbassyChatHandoffStepComponent,
  BrandEmbassyCustomFields,
  WidgetConfigRequest,
  RestartConversationRequest,
  RestartIntentConversationRequest,
  HyperlinkRedirectStepComponent,
  SendFreeFormQueryRequest,
  IntentConversationResponse,
  CsatFeedbackRequestFields,
  QuickFeedbackRequestFields,
  ListOption,
  PresignedUrlRequest,
  TriggerEventStepComponent,
  TriggerEventStepContextVariable,
  HandoffStepComponent,
  GorgiasChatHandoffStepComponent,
  TalkdeskChatHandoffStepComponent,
  TriggerEventStepAdditionalContext,
  FreshchatChatHandoffStepComponent,
  SalesforceMessagingHandoffStepComponent,
  InitIntentConversationRequest,
  UpdateIntentConversationRequest,
  SendFeedbackRequest,
  ImageStepComponent,
  EmbedStepComponent,
  LiveChatChatHandoffStepComponent,
  WidgetMode,
  ForethoughtLiveChatHandoffComponent,
  SuncoLiveChatComponent,
  LiveChatSendMessageRequest,
  ConversationNotFoundError,
  AttemptedToFetchRedactedConversationError,
  UpdateHelpdeskConversationRequest,
  KustomerCallbackResponse,
  StreamingResponse,
  LiveChatAgentMessageEventData,
  LiveChatArticle,
  HandoffData,
  LiveChatIntegration,
  ZendeskLiveChatIntegrationConfig,
  SalesforceMessagingLiveChatIntegrationConfig,
  EventMessageComponent,
  SubmitInteractiveEmailDeflectionFeedbackResponse,
  BadRequestError,
  TooManyRequestsError,
  WidgetComponentsResponse,
  SalesforceVisibleFields,
  SendConversationAlertRequest,
  AiInstructionFilters,
  ContentSnippetFilters,
  LiveChatSendAgentMessageResponse,
  UpdateForethoughtLiveChatHelpdeskConversationIdResponse,
  GladlyChatHandoffStepComponent,
  ZendeskSipCallStepComponent,
  ZendeskSipCallTriggerStepComponent,
  HubspotChatHandoffStepComponent,
  ProductRecommendationStepComponent,
};

export {
  isAttachmentAnalysisUploadStepComponent,
  isCsatFeedbackRequestFields,
  isEventMessageComponent,
  isFormComponent,
  isTextMessageComponent,
  isWorkflowTagSelectorComponent,
  isZendeskAttachmentUploadStepComponent,
  isSelectIntentComponent,
  isArticleComponent,
  isForethoughtLiveChatHandoffComponent,
  isSuncoLiveChatComponent,
  isPusherLiveChatIntegration,
  isUserMessageComponent,
  isButtonStepWithHideFreeFormQueryField,
  isPreviewLogStreamingResponse,
  isOauthSignInComponent,
};
